import { FieldProps } from '@rjsf/core';
import React from 'react';
import { useIntl, FormattedMessage } from 'lib/intl';
import { useDocumentParams } from '../../../pages';
import { ReliningServices } from '../index';
import { Box } from '../../../components/layout';
import { Button, Label } from '../../../components/form-elements';
import * as Icons from '../../../components/icons';
import { LocaleKey } from '../../../translations';
import { Image } from '../../../components/Image';
import { HelperText } from '../../../components/form-elements/Forms/FieldEditText';
import { Textarea } from '../../../components/Textarea/Textarea';
import { Text } from '../../../components/typography';
import { Spacer } from '../../../components/Spacer/Spacer';

type ReliningMachineData = {
  imageUrl?: string;
  fileName?: string;
  timestamp?: number;
  description?: string;
};

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];
const ACCEPTED_FILE_EXTENSIONS = '.jpg,.jpeg,.png,image/jpeg,image/png';
const MAX_FILE_SIZE_BYTES = 12 * 1024 * 1024; // 12 MB

/*
 * Reusable "Machine Picture" field shared by every relining installation form
 * (Spray, Liner, Branch and Other). Rendered right after the installation
 * document upload field.
 *
 * - Lets the user upload a single picture of the machine from the device
 *   gallery (jpg/jpeg/png, <= 12 MB). The button and the picture are optional.
 * - Stores the image publicly in the documents S3 bucket via the shared
 *   relining upload mechanism and writes the resulting public URL into the
 *   form data so it persists and can be embedded in the certificate.
 * - Provides an optional "Describe the Machine" text field that never blocks
 *   submission when left empty.
 */
export function ReliningMachinePicture(props: FieldProps<ReliningMachineData>) {
  const params = useDocumentParams();
  const intl = useIntl();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [errorKey, setErrorKey] = React.useState<LocaleKey | null>(null);

  const { uploadReliningMachinePicture, isLoading } = ReliningServices.useUploadReliningMachinePicture({
    onSuccess: (data) => {
      props.onChange({
        ...props.formData,
        imageUrl: data.uploadUrl,
        fileName: data.fileName,
        timestamp: data.timestamp,
      });
    },
  });

  const hasImage = Boolean(props.formData?.imageUrl);

  const title = props.uiSchema['ui:title']
    ? intl.formatMessage({ id: props.uiSchema['ui:title'] })
    : '';

  function handleSelectFile() {
    setErrorKey(null);
    inputRef.current?.click();
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setErrorKey('relining.machinePicture.error.fileType');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorKey('relining.machinePicture.error.fileSize');
      return;
    }

    setErrorKey(null);
    uploadReliningMachinePicture({
      documentId: params.documentId,
      templateId: params.templateId,
      file,
    });
  }

  function handleRemoveImage() {
    setErrorKey(null);
    // Only clear the image, keep any description the user has typed.
    props.onChange({
      ...props.formData,
      imageUrl: undefined,
      fileName: undefined,
      timestamp: undefined,
    });
  }

  function handleDescriptionChange(description: string) {
    props.onChange({
      ...props.formData,
      description,
    });
  }

  return (
    <Box sx={{ mt: -2 }}>
      {title && (
        <React.Fragment>
          <Text as="h2" variant="h2">
            {title}
          </Text>

          <Spacer y={3} />
        </React.Fragment>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_EXTENSIONS}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {!hasImage && (
        <Button
          disabled={props.disabled}
          isLoading={isLoading}
          prependIcon={(
            <Box sx={{ display: 'inline-flex', mr: -1, color: 'white.50' }}>
              <Icons.FileUpload />
            </Box>
          )}
          type="button"
          onClick={() => handleSelectFile()}
          intlId="relining.machinePicture.uploadButton"
        />
      )}

      {hasImage && (
        <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
          <a
            href={props.formData?.imageUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              src={props.formData?.imageUrl}
              alt={intl.formatMessage({ id: 'relining.machinePicture.title' })}
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: 240,
                borderRadius: 1,
                cursor: 'pointer',
              }}
            />
          </a>

          {!props.disabled && (
            <button
              type="button"
              onClick={handleRemoveImage}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0, 0, 0, 0.5)',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'inline-flex',
                padding: 6,
                color: '#fff',
              }}
            >
              <Icons.Delete fill="currentColor" />
            </button>
          )}
        </Box>
      )}

      {errorKey && (
        <HelperText isError>
          <FormattedMessage id={errorKey} />
        </HelperText>
      )}

      <Spacer y={4} />

      <Label
        variant="jsonInputLabel"
        labelIntlId="relining.machinePicture.describeLabel"
      />

      <Spacer y={2} />

      <Textarea
        name={`${props.idSchema.$id}_description`}
        label=""
        isError={false}
        isLabelAlwaysLifted={false}
        backgroundColor="outlinedTextWidget.background"
        disabled={props.disabled}
        value={props.formData?.description ?? ''}
        onChange={(event) => handleDescriptionChange(event.target.value)}
        placeholder={intl.formatMessage({
          id: 'relining.machinePicture.describePlaceholder',
        })}
      />
    </Box>
  );
}

